import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, ArrowRight } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { submitContactForm } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const ContactSection = () => {
  const { toast } = useToast();
  const { language } = useLanguage();
  const t = translations[language];
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    priceRange: '',
    observations: '',
    privacyAccepted: false
  });

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.privacyAccepted) {
      toast({
        title: language === 'pt' ? 'Erro' : 'Error',
        description: language === 'pt' ? 'Por favor, aceite a Política de Privacidade.' : 'Please accept the Privacy Policy.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await submitContactForm(formData);
      
      toast({
        title: language === 'pt' ? 'Mensagem enviada!' : 'Message sent!',
        description: response.message || (language === 'pt' ? 'Entraremos em contacto em breve.' : 'We will contact you soon.')
      });
      
      // Reset form
      setFormData({ 
        name: '', 
        email: '', 
        phone: '', 
        location: '', 
        priceRange: '', 
        observations: '',
        privacyAccepted: false 
      });
    } catch (error) {
      toast({
        title: language === 'pt' ? 'Erro' : 'Error',
        description: error.detail || (language === 'pt' ? 'Ocorreu um erro ao enviar a mensagem. Tente novamente.' : 'An error occurred while sending the message. Try again.'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const whatsappText = language === 'pt' 
    ? 'Ol%C3%A1,%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es'
    : 'Hello,%20I%20would%20like%20more%20information';
  const whatsappLink = `https://wa.me/${companyInfo.contact.whatsapp}?text=${whatsappText}`;

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-display font-light text-brand-text mb-6">{t.contactTitle}</h2>
          <p className="text-lg font-body text-brand-text-secondary max-w-3xl mx-auto">{t.contactSubtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-display font-light text-brand-text mb-8">{t.informations}</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 rounded-full bg-brand-primary flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-brand-accent" />
                  </div>
                  <div>
                    <p className="font-display font-medium text-brand-text mb-1">{t.email}</p>
                    <a href={`mailto:${companyInfo.contact.email}`} className="font-body text-brand-text-secondary hover:text-brand-primary transition-colors">
                      {companyInfo.contact.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 rounded-full bg-brand-primary flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-brand-accent" />
                  </div>
                  <div>
                    <p className="font-display font-medium text-brand-text mb-1">{t.phone}</p>
                    <a href={`tel:${companyInfo.contact.phone}`} className="font-body text-brand-text-secondary hover:text-brand-primary transition-colors">
                      {companyInfo.contact.phone}
                    </a>
                  </div>
                </div>

                <div className="p-8 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200">
                  <h4 className="font-display font-medium text-brand-text mb-2 text-lg">{t.whatsappDirect}</h4>
                  <p className="text-sm font-body text-brand-text-secondary mb-4">{t.whatsappText}</p>
                  <Button 
                    asChild
                    className="bg-green-500 hover:bg-green-600 text-white w-full"
                  >
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                      {t.openWhatsapp}
                    </a>
                  </Button>
                </div>

                <div className="text-sm font-body text-brand-text-secondary space-y-1 pt-6 border-t border-brand-accent">
                  <p className="font-display"><strong>{companyInfo.contact.company}</strong></p>
                  <p>NIPC: {companyInfo.contact.nipc}</p>
                  <p>{companyInfo.contact.ami}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h3 className="text-3xl font-display font-light text-brand-text mb-8">{t.sendMessage}</h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder={`${t.name} *`}
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full"
                />
              </div>
              <div>
                <Input
                  type="email"
                  name="email"
                  placeholder={`${t.email} *`}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full"
                />
              </div>
              <div>
                <Input
                  type="tel"
                  name="phone"
                  placeholder={`${t.phone} *`}
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full"
                />
              </div>
              <div>
                <Input
                  type="text"
                  name="location"
                  placeholder={t.preferredLocation}
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full"
                />
              </div>
              <div>
                <Input
                  type="text"
                  name="priceRange"
                  placeholder={t.priceRange}
                  value={formData.priceRange}
                  onChange={handleChange}
                  className="w-full"
                />
              </div>
              <div>
                <Textarea
                  name="observations"
                  placeholder={t.observations}
                  value={formData.observations}
                  onChange={handleChange}
                  rows={4}
                  className="w-full"
                />
              </div>
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="privacy"
                  name="privacyAccepted"
                  checked={formData.privacyAccepted}
                  onChange={handleChange}
                  required
                  className="mt-1"
                />
                <label htmlFor="privacy" className="text-sm text-brand-text-secondary">
                  {t.privacyAccept} <a href="#" className="text-brand-text underline">{t.privacyPolicyLink}</a> *
                </label>
              </div>
              <Button type="submit" className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white py-6 font-body text-lg" disabled={loading}>
                {loading ? (language === 'pt' ? 'Enviando...' : 'Sending...') : t.sendButton}
              </Button>
            </form>
          </div>
        </div>

        {/* Botão Saiba Mais */}
        <div className="text-center mt-12">
          <Link 
            to="/contacto"
            className="inline-flex items-center gap-2 text-brand-primary hover:text-brand-primary-hover font-body font-medium text-lg transition-colors group"
          >
            {language === 'pt' ? 'Página de Contacto Completa' : 'Full Contact Page'}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;